import React, { useState, useEffect } from "react";
import { ProductSheet } from "../../types/product-sheet.types";
import { productSheetApi } from "../../services/product-sheet.service";
import { useI18n } from "../../i18n";

export default function ProductSheetPage() {
  const { t, code } = useI18n();
  const [productSheets, setProductSheets] = useState<ProductSheet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchProductSheets = async () => {
      try {
        setLoading(true);
        const response = await productSheetApi.getAllProductSheets();
        setProductSheets(response.data);
      } catch (err) {
        console.error("Error fetching product sheets:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchProductSheets();
  }, []);

  const handleDownload = async (sheetId: string) => {
    try {
      const response = await productSheetApi.downloadProductSheet(sheetId);
      // Open the presigned URL in a new tab
      window.open(response.data.downloadUrl, "_blank");
    } catch (err) {
      console.error("Error downloading product sheet:", err);
      alert(t("productSheets.errors.downloadFailed"));
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(code, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case "technical sheet":
        return "fa-cogs";
      case "user guide":
        return "fa-book";
      case "api documentation":
        return "fa-code";
      case "installation guide":
        return "fa-download";
      case "quick start guide":
        return "fa-play";
      default:
        return "fa-file-text";
    }
  };

  return (
    <div className="flex flex-col flex-1 h-full">
      {/* Header */}
      <div className="flex items-center justify-between h-20 px-8 bg-white border-b border-gray-200 sticky top-0 z-20">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{t("productSheets.title")}</h1>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            {t("productSheets.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* Notification bell */}
          <button className="relative p-2 rounded-full hover:bg-gray-100">
            <svg
              width="20"
              height="20"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="text-gray-400"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 px-8 pb-8">
        <div className="bg-white rounded-xl shadow p-8 mt-4">
          <p className="text-gray-700 mb-2">
            {t("productSheets.intro")}
          </p>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800"></div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg mb-6">
              {t("productSheets.errors.loadFailed")}
            </div>
          ) : (
            <>
              <p className="text-xs text-gray-400 mb-6">
                {t("common.lastUpdated", {
                  date:
                    productSheets.length > 0
                      ? formatDate(productSheets[0].updatedAt)
                      : t("common.notAvailable"),
                })}
              </p>

              <div className="space-y-4">
                {productSheets.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="flex flex-col items-center justify-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <i className="fa-solid fa-file-text text-gray-400 text-2xl"></i>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {t("productSheets.empty.title")}
                      </h3>
                      <p className="text-gray-500 text-sm">
                        {t("productSheets.empty.body")}
                      </p>
                    </div>
                  </div>
                ) : (
                  productSheets.map((sheet) => (
                    <div
                      key={sheet.id}
                      className="flex items-center justify-between bg-gray-50 rounded-lg px-6 py-4"
                    >
                      <div className="flex items-center gap-4">
                        {/* Dynamic icon based on category */}
                        <div className="bg-blue-100 rounded-lg p-2">
                          <i
                            className={`fa-solid ${getCategoryIcon(
                              sheet.category
                            )} text-blue-600 text-xl`}
                          ></i>
                        </div>
                        <div>
                          <span className="text-gray-900 font-medium text-base block">
                            {sheet.sheetName}
                          </span>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded-full">
                              v{sheet.version}
                            </span>
                            <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                              {sheet.category}
                            </span>
                            {sheet.fileSize && (
                              <span className="text-xs text-gray-500">
                                {sheet.fileSize}
                              </span>
                            )}
                            <span className="text-xs text-gray-500">
                              {t("productSheets.downloadsCount", {
                                count: sheet.downloads,
                              })}
                            </span>
                          </div>
                          {sheet.description && (
                            <p className="text-sm text-gray-600 mt-1">
                              {sheet.description}
                            </p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDownload(sheet.id)}
                        disabled={!sheet.downloadKey}
                        className="bg-gray-900 text-white px-6 py-2 rounded-lg flex items-center gap-2 text-sm font-medium hover:bg-gray-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <i className="fa-solid fa-download"></i>
                        {t("productSheets.download")}
                      </button>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
